import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
} from 'react';

interface AppContextProps {
  isLoggedIn: boolean;
  handleLogin: () => void;
  handleSignOut: () => void;
  registrationSuccess: boolean;
  handleRegister: () => void;
  resetRegistrationSuccess: () => void;
  isLargeScreen: boolean;
  handleLinkClick: () => void;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  isOpen: boolean;
  toggleDropdown: () => void;
  togglePasswordVisibility: (status: 'password' | 'confirm-password') => void;
  showPassword: boolean;
  showConfirmPassword?: boolean;
}

interface AppProviderProps {
  children: ReactNode;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const useAppContext = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    sessionStorage.getItem('isLoggedIn') === 'true'
  );
  const [registrationSuccess, setRegistrationSuccess] = useState<boolean>(false);
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(window.innerWidth >= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const handleLinkClick = useCallback(() => {
    setIsSidebarOpen(false);
    setIsOpen(false);
  }, []);

  const togglePasswordVisibility = useCallback(
    (status: 'password' | 'confirm-password') => {
      if (status === 'confirm-password') {
        setShowConfirmPassword((prevShow) => !prevShow);
      } else {
        setShowPassword((prevShow) => !prevShow);
      }
    },
    []
  );

  const handleLogin = useCallback(() => {
    setIsLoggedIn(true);
    sessionStorage.setItem('isLoggedIn', 'true');
  }, []);

  const handleRegister = useCallback(() => {
    setRegistrationSuccess(true);
  }, []);

  const resetRegistrationSuccess = useCallback(() => {
    setRegistrationSuccess(false);
  }, []);

  const handleSignOut = useCallback(() => {
    sessionStorage.clear();
    localStorage.clear();
    setIsLoggedIn(false);
    sessionStorage.setItem('isLoggedIn', 'false');
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isSidebarOpen && window.innerWidth >= 1024) {
      setIsSidebarOpen(false);
    } else {
      setIsSidebarOpen((prevIsOpen) => !prevIsOpen);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <AppContext.Provider
      value={{
        isLoggedIn,
        handleLogin,
        handleSignOut,
        registrationSuccess,
        handleRegister,
        handleLinkClick,
        resetRegistrationSuccess,
        isLargeScreen,
        isSidebarOpen,
        toggleSidebar,
        isOpen,
        toggleDropdown,
        togglePasswordVisibility,
        showPassword,
        showConfirmPassword,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};